/**
 * Presentation helpers for sortable list pages:
 *   • SortHeader — a clickable `<th>` cell with an ascending / descending
 *     arrow indicator. Uses `data-testid={sort-<key>}` so E2E tests
 *     can drive sorts without hunting for column labels.
 *   • SortSelect — the same functionality but as a compact dropdown,
 *     for card-grid pages where a full `<thead>` doesn't fit.
 *   • fmtDate — UK-friendly short date, tolerant of the two most
 *     common server-side date shapes (`YYYY-MM-DD` and ISO strings
 *     with time). Falls back to the raw string on parse failure so
 *     unusual server output stays visible instead of "Invalid Date".
 */
import React from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';

export const SortHeader = ({ label, sortKey, sort, onSort, align = 'left', className = '' }) => {
  const active = sort.key === sortKey;
  const Icon = active ? (sort.dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  const alignCls = align === 'right' ? 'justify-end' : 'justify-start';
  return (
    <th className={`px-4 py-3 font-medium text-slate-600 select-none ${className}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 hover:text-slate-900 transition-colors ${alignCls} ${
          active ? 'text-slate-900' : ''
        }`}
        data-testid={`sort-${sortKey}`}
      >
        {label}
        <Icon className={`h-3 w-3 ${active ? 'opacity-100' : 'opacity-40'}`} />
      </button>
    </th>
  );
};

export const SortSelect = ({ label = 'Sort by', options, sort, onSort, className = '' }) => (
  <div className={`inline-flex items-center gap-2 text-xs ${className}`}>
    <span className="text-slate-500">{label}:</span>
    <select
      className="border border-slate-200 rounded px-2 py-1 bg-white text-slate-800"
      value={sort.key || ''}
      onChange={(e) => {
        // Two-step select behaviour: changing the key just switches
        // column with the hook's own default direction. Users click
        // the arrow button next to it to flip direction.
        if (e.target.value && e.target.value !== sort.key) onSort(e.target.value);
      }}
      data-testid="sort-select-key"
    >
      {options.map(o => (
        <option key={o.key} value={o.key}>{o.label}</option>
      ))}
    </select>
    <button
      type="button"
      onClick={() => sort.key && onSort(sort.key)}
      className="border border-slate-200 rounded px-1.5 py-1 bg-white hover:bg-slate-50 text-slate-700"
      title={sort.dir === 'asc' ? 'Ascending — click to flip' : 'Descending — click to flip'}
      data-testid="sort-select-dir"
    >
      {sort.dir === 'asc'
        ? <ArrowUp className="h-3.5 w-3.5" />
        : <ArrowDown className="h-3.5 w-3.5" />}
    </button>
  </div>
);

import { formatDate } from '../utils/date';

/**
 * `fmtDate` used to render the UK short form "08 Jul 2026". Now
 * delegates to the app-wide `formatDate` helper so every list uses
 * the same "8th July 2026" shape — keep it re-exported here so
 * existing imports keep working.
 */
export const fmtDate = (v) => formatDate(v);

export default SortHeader;
